import { Box, Typography } from "@mui/material"
import "../../App.css"
import COLORS from "../../common/FortesColors"
import { Currency } from "../../common/Models"
import { FortesSwitch } from "../FortesInputs/FortesSwitch"
import { scrollbarSX } from "../FortesInputs/FortesScrollbar"
import { useState } from "react"
import { FortesUnderlineButton } from "../FortesInputs/FortesButtons"
import { mm2tocm2, mm2tom2, mmtocm } from "../../common/Conversion"
import { useAdminContext } from "../../AdminContext"

export default function PriceBox(props: {
  totalAreaMM2: number
  lengthMM: number
  usedAreaMM2: number
  wasted: number
  pricePerM2: number
  priceStats: { [key: string]: { one: number; all: number; cnt: number } }
  currency: Currency
  onCurrencyChange: (newCurrency: Currency) => void
}): JSX.Element {
  const { isAdmin } = useAdminContext()
  const [collapsed, setCollapse] = useState<boolean>(true)
  let ids = Object.keys(props.priceStats || {})
  ids.sort()
  const idsLen =
    ids.length + ids.filter((id) => props.priceStats[id].cnt > 1).length
  const cutOutBoxSx =
    idsLen <= 5
      ? {}
      : collapsed
      ? { maxHeight: "10vh", overflowY: "hidden" }
      : {
          maxHeight: "75vh",
          minHeight: "75vh",
          overflowY: "auto",
          ...scrollbarSX,
        }

  const typographySx = {
    paddingRight: "20px",
    color: COLORS.darkPurple,
    fontSize: "100%",
    fontWeight: 600,
    maxWidth: "200px",
    maxHeight: "50px",
  }

  const fullOrderCost = (
    mm2tom2(props.totalAreaMM2) * props.pricePerM2
  ).toFixed(2)
  const usedFoilLengthCM = mmtocm(props.lengthMM).toFixed(2)
  const totalFoilAreaCM2 = mm2tocm2(props.totalAreaMM2).toFixed(2)
  const foilUsedCM2 = mm2tocm2(props.usedAreaMM2).toFixed(2)
  const foilUsedPERC = Math.round(
    (props.usedAreaMM2 / props.totalAreaMM2) * 100
  )

  return (
    <div
      style={{
        margin: "20px",
        width: 400,
        background: "white",
      }}
    >
      <Comment text={"Price may vary ±5% based on foil usage."} />
      <br />

      <Typography {...typographySx}>Total price</Typography>

      <Cell
        key_="Full order cost:"
        value={`± ${fullOrderCost} ${props.currency}`}
      />
      <br />

      {ids.length > 0 && (
        <>
          {idsLen > 5 ? (
            <Box
              display="flex"
              justifyContent="space-between"
              flexDirection="row"
            >
              <Typography {...typographySx}>Cut-out prices</Typography>
              <FortesUnderlineButton onClick={() => setCollapse(!collapsed)}>
                {collapsed ? "show all" : "hide"}
              </FortesUnderlineButton>
            </Box>
          ) : (
            <Typography {...typographySx}>Cut-out prices</Typography>
          )}
          <Box sx={{ ...cutOutBoxSx }}>
            {ids.map((id) => {
              return (
                <>
                  {props.priceStats[id].cnt > 1 && (
                    <Cell
                      key_={`Price for ${props.priceStats[id].cnt}× ${id}:`}
                      value={`± ${props.priceStats[id].all.toFixed(2)} ${
                        props.currency
                      }`}
                    />
                  )}

                  <Cell
                    key_={`Price for 1× ${id}:`}
                    value={`± ${props.priceStats[id].one.toFixed(2)} ${
                      props.currency
                    }`}
                    isExtra={props.priceStats[id].cnt > 1}
                  />
                </>
              )
            })}
          </Box>
          {idsLen > 5 && collapsed && (
            <>
              <FortesUnderlineButton onClick={() => setCollapse(!collapsed)}>
                ...
              </FortesUnderlineButton>
              <br />
            </>
          )}
          <br />
        </>
      )}
      {collapsed && (
        <>
          <Typography {...typographySx}>Area usage</Typography>

          <Cell key_="Used foil lenght:" value={`${usedFoilLengthCM} cm`} />
          <Cell
            key_="Total area of the foil:"
            value={`${totalFoilAreaCM2} cm²`}
            isExtra={true}
          />
          <Cell
            key_="Foil used:"
            value={`${foilUsedCM2} cm² (${foilUsedPERC} %)`}
          />
          {isAdmin && (
            <Cell
              key_="Money wasted on empty space:"
              value={`± ${props.wasted.toFixed(2)} ${props.currency}`}
            />
          )}
          <br />

          <Box
            sx={{
              marginTop: "0px",
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            <FortesSwitch
              options={[
                {
                  name: "CZK",
                  onClick: () => {
                    props.onCurrencyChange("CZK")
                  },
                },
                {
                  name: "EUR",
                  onClick: () => {
                    props.onCurrencyChange("EUR")
                  },
                },
              ]}
              value={props.currency}
              width="Small"
            />
          </Box>
        </>
      )}
    </div>
  )
}

function Comment(props: { text: string }): JSX.Element {
  const sx = {
    paddingRight: "20px",
    color: "gray",
    fontSize: "90%",
    fontWeight: 500,
    maxWidth: "400px",
    maxHeight: "50px",
  }

  return (
    <Box
      display="flex"
      justifyContent="space-between"
      sx={{
        paddingBlockEnd: "10px",
        paddingBlockStart: "10px",
        borderBlockEnd: `1px solid ${COLORS.separator}`,
      }}
    >
      <Typography {...sx}>{props.text}</Typography>
    </Box>
  )
}

function Cell(props: {
  key_: string
  value: string
  isExtra?: boolean
}): JSX.Element {
  const sx = {
    paddingRight: "20px",
    color: props.isExtra ? "gray" : COLORS.darkPurple,
    fontSize: "90%",
    fontWeight: 500,
    maxWidth: "200px",
    maxHeight: "50px",
  }

  return (
    <Box
      display="flex"
      justifyContent="space-between"
      sx={{
        paddingBlockEnd: "10px",
        paddingBlockStart: "10px",
        borderBlockEnd: `1px solid ${COLORS.separator}`,
      }}
    >
      <Typography {...sx}>{props.key_}</Typography>

      <Typography {...sx}>{props.value}</Typography>
    </Box>
  )
}
